
import React, { useState, useCallback } from 'react';
import './KanbanPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import { useToast } from '../../context/ToastContext';

type Priority = 'low' | 'medium' | 'high' | 'urgent';
type ColumnId = 'backlog' | 'todo' | 'inprogress' | 'review' | 'done';

interface Task {
  id: string;
  title: string;
  description?: string;
  priority: Priority;
  assignee?: string;
  dueDate?: string;
  labels: string[];
  progress?: number;
}

interface Column {
  id: ColumnId;
  title: string;
  tasks: Task[];
}

const PRIORITY_COLORS: Record<Priority, string> = {
  low: '#22C55E',
  medium: '#F59E0B',
  high: '#EF4444',
  urgent: '#DC2626',
};

const INITIAL_COLUMNS: Column[] = [
  {
    id: 'backlog',
    title: 'Backlog',
    tasks: [
      { id: '1', title: 'Research competitor features', priority: 'low', labels: ['research'], assignee: 'SM', description: 'Analyze top 5 competitors' },
      { id: '2', title: 'Update documentation', priority: 'low', labels: ['docs'], assignee: 'JR' },
    ],
  },
  {
    id: 'todo',
    title: 'To Do',
    tasks: [
      { id: '3', title: 'Design new dashboard layout', priority: 'medium', labels: ['design', 'ui'], assignee: 'EV', dueDate: 'Feb 26' },
      { id: '4', title: 'API rate limiting', priority: 'high', labels: ['backend'], assignee: 'TN', description: 'Implement rate limiting for API endpoints' },
    ],
  },
  {
    id: 'inprogress',
    title: 'In Progress',
    tasks: [
      { id: '5', title: 'Implement AI chat streaming', priority: 'high', labels: ['ai', 'feature'], assignee: 'PS', progress: 65, dueDate: 'Feb 25' },
      { id: '6', title: 'Fix mobile navigation', priority: 'urgent', labels: ['bug', 'mobile'], assignee: 'ML', progress: 40 },
    ],
  },
  {
    id: 'review',
    title: 'Review',
    tasks: [
      { id: '7', title: 'Calendar event sync', priority: 'medium', labels: ['feature'], assignee: 'SM', progress: 90 },
    ],
  },
  {
    id: 'done',
    title: 'Done',
    tasks: [
      { id: '8', title: 'Authentication flow', priority: 'high', labels: ['auth'], assignee: 'JR' },
      { id: '9', title: 'Email templates', priority: 'medium', labels: ['email'], assignee: 'EV' },
    ],
  },
];

const LABEL_COLORS: Record<string, string> = {
  research: '#8B5CF6',
  docs: '#64748B',
  design: '#EC4899',
  ui: '#06B6D4',
  backend: '#22C55E',
  ai: '#F59E0B',
  feature: '#1282D6',
  bug: '#EF4444',
  mobile: '#8B5CF6',
  auth: '#22C55E',
  email: '#06B6D4',
};

export default function KanbanPage() {
  const [columns, setColumns] = useState<Column[]>(INITIAL_COLUMNS);
  const [draggedTask, setDraggedTask] = useState<{ task: Task; fromColumn: ColumnId } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium' as Priority });
  const { addToast } = useToast();

  const handleDragStart = useCallback((task: Task, fromColumn: ColumnId) => {
    setDraggedTask({ task, fromColumn });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((toColumn: ColumnId) => {
    if (!draggedTask) return;
    const { task, fromColumn } = draggedTask;
    if (fromColumn === toColumn) {
      setDraggedTask(null);
      return;
    }

    setColumns(cols =>
      cols.map(col => {
        if (col.id === fromColumn) {
          return { ...col, tasks: col.tasks.filter(t => t.id !== task.id) };
        }
        if (col.id === toColumn) {
          return { ...col, tasks: [...col.tasks, task] };
        }
        return col;
      })
    );
    setDraggedTask(null);
    addToast('success', `Moved "${task.title}" to ${toColumn}`);
  }, [draggedTask, addToast]);

  const createTask = () => {
    if (!newTask.title.trim()) return;
    const task: Task = {
      id: Date.now().toString(),
      title: newTask.title,
      description: newTask.description,
      priority: newTask.priority,
      labels: [],
      assignee: 'You',
    };
    setColumns(cols =>
      cols.map(col =>
        col.id === 'backlog' ? { ...col, tasks: [...col.tasks, task] } : col
      )
    );
    setNewTask({ title: '', description: '', priority: 'medium' });
    setShowCreateModal(false);
    addToast('success', 'Task created!');
  };

  const totalTasks = columns.reduce((sum, col) => sum + col.tasks.length, 0);
  const completedTasks = columns.find(c => c.id === 'done')?.tasks.length || 0;

  return (
    <div className="kanban-page">
      <PageHeader
        title="Kanban Board"
        breadcrumb={['Business', 'Kanban']}
        actions={
          <>
            <Badge variant="ocean">{completedTasks}/{totalTasks} Complete</Badge>
            <Button icon="+" size="sm" onClick={() => setShowCreateModal(true)}>New Task</Button>
          </>
        }
      />

      <div className="kanban-board">
        {columns.map(column => (
          <div
            key={column.id}
            className="kanban-column"
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(column.id)}
          >
            <div className="kanban-column__header">
              <span className="kanban-column__title">{column.title}</span>
              <Badge variant="default">{column.tasks.length}</Badge>
            </div>
            <div className="kanban-column__tasks">
              {column.tasks.map(task => (
                <div
                  key={task.id}
                  className="kanban-card"
                  draggable
                  onDragStart={() => handleDragStart(task, column.id)}
                >
                  <div
                    className="kanban-card__priority-stripe"
                    style={{ background: PRIORITY_COLORS[task.priority] }}
                  />
                  <div className="kanban-card__content">
                    <div className="kanban-card__header">
                      <span className="kanban-card__title">{task.title}</span>
                      {task.assignee && (
                        <span className="kanban-card__assignee">{task.assignee}</span>
                      )}
                    </div>
                    {task.description && (
                      <p className="kanban-card__desc">{task.description}</p>
                    )}
                    {task.labels.length > 0 && (
                      <div className="kanban-card__labels">
                        {task.labels.map(label => (
                          <span
                            key={label}
                            className="kanban-label"
                            style={{ background: `${LABEL_COLORS[label] || '#64748B'}22`, color: LABEL_COLORS[label] || '#64748B' }}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    )}
                    {task.progress !== undefined && (
                      <div className="kanban-card__progress">
                        <div className="kanban-progress-bar">
                          <div
                            className="kanban-progress-fill"
                            style={{ width: `${task.progress}%`, background: PRIORITY_COLORS[task.priority] }}
                          />
                        </div>
                        <span className="kanban-progress-text">{task.progress}%</span>
                      </div>
                    )}
                    {task.dueDate && (
                      <div className="kanban-card__due">
                        <span>Due: {task.dueDate}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              className="kanban-add-btn"
              onClick={() => { setShowCreateModal(true); }}
            >
              + Add Task
            </button>
          </div>
        ))}
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Create Task</h3>
              <button className="modal__close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="modal__body">
              <label className="form-label">Title</label>
              <input
                className="form-input"
                value={newTask.title}
                onChange={e => setNewTask(t => ({ ...t, title: e.target.value }))}
                placeholder="Task title..."
              />
              <label className="form-label">Description</label>
              <textarea
                className="form-input form-textarea"
                value={newTask.description}
                onChange={e => setNewTask(t => ({ ...t, description: e.target.value }))}
                placeholder="Optional description..."
              />
              <label className="form-label">Priority</label>
              <select
                className="form-input"
                value={newTask.priority}
                onChange={e => setNewTask(t => ({ ...t, priority: e.target.value as Priority }))}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="modal__footer">
              <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={createTask}>Create</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
